<template>
  <!-- Renders ONLY when the tenant has published FAQ items — no empty
       shells (C5). Content comes from the same endpoint as /faq, so the
       landing page never invents answers. -->
  <section v-if="items.length" id="faq" class="faq" aria-labelledby="faq-title">
    <div class="faq-inner">
      <header class="faq-head" data-reveal="up">
        <span class="faq-eyebrow">{{ $t('landing.faq_label') }}</span>
        <h2 id="faq-title" class="faq-title">{{ $t('landing.faq_title') }}</h2>
        <p class="faq-sub">{{ $t('landing.faq_sub') }}</p>
      </header>

      <div class="faq-list" data-reveal="up">
        <details v-for="(item, idx) in items" :key="idx" class="faq-item">
          <summary class="faq-q">
            <span class="faq-plus" aria-hidden="true">
              <span class="ph"></span>
              <span class="pv"></span>
            </span>
            <span class="faq-q-text">{{ item.question }}</span>
          </summary>
          <div class="faq-a">{{ item.answer }}</div>
        </details>
      </div>

      <div class="faq-more" data-reveal="up">
        <NuxtLink :to="localePath('/faq')" class="faq-more-link">
          {{ $t('landing.faq_view_all') }}
          <span aria-hidden="true">→</span>
        </NuxtLink>
      </div>
    </div>
  </section>
</template>

<script setup>
const localePath = useLocalePath()

// Same endpoint the /faq page reads; the landing section shows the first
// six entries (the purchase-blocking objections belong at the top of the
// tenant's FAQ list).
const { data } = await useFetch('/api/public/faq', {
  key: 'landing-faq',
  default: () => ({ intro: '', items: [] })
})

const items = computed(() => (data.value?.items || []).slice(0, 6))
</script>

<style scoped>
/* Mobile-first. Ladder: 480 / 640 / 768 / 1024 / 1280 (variables.css). */

.faq {
  position: relative;
  padding: var(--landing-section-pad) 0;
}

.faq-inner {
  max-width: 820px;
  margin: 0 auto;
  padding: 0 var(--space-6);
}

.faq-head {
  text-align: center;
  margin-bottom: var(--space-10);
}

.faq-eyebrow {
  display: inline-block;
  font-family: var(--font-heading);
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.24em;
  text-transform: uppercase;
  color: var(--primary);
  margin-bottom: var(--space-4);
}

.faq-title {
  font-family: var(--font-heading);
  font-size: var(--landing-title-size);
  font-weight: 800;
  line-height: 1.05;
  text-transform: uppercase;
  letter-spacing: -0.01em;
  margin: 0 0 var(--space-4);
  text-wrap: balance;
}

.faq-sub {
  font-size: var(--landing-sub-size);
  line-height: 1.6;
  color: var(--text-muted);
  margin: 0;
}

.faq-list {
  border: 1px solid var(--border);
  border-radius: var(--radius-xl);
  background: var(--surface-color);
  padding: var(--space-2) var(--space-5);
}

.faq-item {
  border-bottom: 1px solid var(--border);
}

.faq-item:last-child {
  border-bottom: none;
}

.faq-q {
  list-style: none;
  display: flex;
  align-items: flex-start;
  gap: var(--space-4);
  padding: var(--space-4) 0;
  min-height: 44px;
  cursor: pointer;
  user-select: none;
  -webkit-tap-highlight-color: transparent;
}

.faq-q::-webkit-details-marker {
  display: none;
}

.faq-q:focus-visible {
  outline: 2px solid var(--primary);
  outline-offset: 2px;
  border-radius: var(--radius-sm);
}

.faq-plus {
  position: relative;
  flex-shrink: 0;
  width: 28px;
  height: 28px;
  margin-top: 1px;
  border-radius: 50%;
  border: 1px solid var(--glass-border);
  background: rgb(255 255 255 / 0.04);
  transition: transform 0.25s ease, border-color 0.25s ease;
}

.faq-plus .ph,
.faq-plus .pv {
  position: absolute;
  background: var(--primary);
  border-radius: 1px;
}

.faq-plus .ph {
  top: 50%;
  left: 8px;
  right: 8px;
  height: 2px;
  transform: translateY(-50%);
}

.faq-plus .pv {
  left: 50%;
  top: 8px;
  bottom: 8px;
  width: 2px;
  transform: translateX(-50%);
  transition: opacity 0.2s ease;
}

.faq-item[open] .faq-plus {
  transform: rotate(45deg);
  border-color: rgb(var(--primary-channels) / 0.4);
}

.faq-q-text {
  font-family: var(--font-heading);
  font-size: 1rem;
  font-weight: 600;
  line-height: 1.4;
  color: var(--text-main);
  padding-top: 3px;
}

@media (min-width: 640px) {
  .faq-q-text {
    font-size: 1.08rem;
  }
}

.faq-a {
  padding: 0 0 var(--space-5) calc(28px + var(--space-4));
  color: var(--text-muted);
  font-size: 0.95rem;
  line-height: 1.7;
  white-space: pre-line;
}

.faq-more {
  display: flex;
  justify-content: center;
  margin-top: var(--space-6);
}

.faq-more-link {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-height: 44px;
  padding: 10px var(--space-4);
  font-size: 0.85rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--primary);
  border-radius: var(--radius-sm);
  transition: color 0.2s ease, background 0.2s ease;
}

.faq-more-link:hover {
  background: rgb(var(--primary-channels) / 0.06);
}

.faq-more-link:focus-visible {
  outline: 2px solid var(--primary);
  outline-offset: 2px;
}
</style>
